import React, { Component } from 'react';
import PropTypes from 'prop-types';

import SlateDialog from 'components/Shared/SlateDialog/SlateDialog';
import BlueButton from 'components/Shared/BlueButton/BlueButton';

// The five Learn More dialogs of the Inspect landing page: a lead, one real screenshot (a light and a
// dark file, switched by CSS), short blocks and at most one static diagram. Request Access sits at the
// end of the content; the footer holds Close only. No animation anywhere in here.
export var LEARN_MORE_ROWS = ['due', 'results', 'repairs', 'skipped', 'picture'];

var IMAGE_DIR = '/images/inspect-landing/';

// Screenshot files, rendered from the dashboard's own build with demo data (tools/inspect-landing-video/capture/landing)
// and shipped at 2x. width and height are the display size, half the file's pixels. theme: 'light' and 'dark' files
// are switched by CSS, a file without a theme shows in both themes.
export var SHOTS = {
  due: [
    { file: 'due-light-2x.png', theme: 'light', width: 870, height: 612, alt: 'dueAltLight' },
    { file: 'due-dark-2x.png', theme: 'dark', width: 870, height: 612, alt: 'dueAltDark' },
  ],
  results: [
    { file: 'results-before-2x.png', width: 400, height: 325, alt: 'resultsBeforeAlt' },
    { file: 'results-after-2x.png', width: 400, height: 500, alt: 'resultsAfterAlt' },
  ],
  repairs: [
    { file: 'repairs-light-2x.png', theme: 'light', width: 496, height: 375, alt: 'repairsAltLight' },
    { file: 'repairs-dark-2x.png', theme: 'dark', width: 496, height: 375, alt: 'repairsAltDark' },
  ],
  skipped: [
    { file: 'skipped-light-2x.png', theme: 'light', width: 1130, height: 261, alt: 'skippedAltLight' },
    { file: 'skipped-dark-2x.png', theme: 'dark', width: 1130, height: 261, alt: 'skippedAltDark' },
  ],
  picture: [
    { file: 'picture-light-2x.png', theme: 'light', width: 1130, height: 675, alt: 'pictureAltLight' },
    { file: 'picture-dark-2x.png', theme: 'dark', width: 1130, height: 675, alt: 'pictureAltDark' },
  ],
};

// The Inspect module's result chips (components/Inspect/style.css); the landing page uses them in its list too.
export function chip(kind, label) {
  return (
    <span key={kind} className={'inspect-result-badge ' + kind}>
      {label}
    </span>
  );
}

export function timedChip(kind, label, time) {
  return (
    <span key={kind} className={'inspect-result-badge ' + kind}>
      {label}
      <span className="inspect-result-badge-divider" />
      <span className="inspect-result-badge-delta">{time}</span>
    </span>
  );
}

// One screenshot frame: the light and the dark file of a row share one frame.
function shotFrame(row, t, extraClass) {
  var shots = SHOTS[row];
  return (
    <div className={'inspect-landing-md-shot' + (extraClass ? ' ' + extraClass : '')}>
      {shots.map(function (shot) {
        var themeClass = shot.theme ? ' inspect-landing-only-' + shot.theme : '';
        return (
          <img
            key={shot.file}
            className={'inspect-landing-md-img' + themeClass}
            src={IMAGE_DIR + shot.file}
            width={shot.width}
            height={shot.height}
            alt={t('inspectLanding:' + shot.alt)}
          />
        );
      })}
    </div>
  );
}

// A single-file frame (the Results pair: one app screen each, same in both themes).
function appShot(row, index, t) {
  var shot = SHOTS[row][index];
  return (
    <div className="inspect-landing-md-shot inspect-landing-md-app">
      <img
        className="inspect-landing-md-img"
        src={IMAGE_DIR + shot.file}
        width={shot.width}
        height={shot.height}
        alt={t('inspectLanding:' + shot.alt)}
      />
    </div>
  );
}

function dueBody(t) {
  return (
    <div>
      <p className="inspect-landing-md-lead">{t('inspectLanding:dueLead')}</p>
      <figure className="inspect-landing-md-fig">
        {shotFrame('due', t)}
        <figcaption>
          <p className="inspect-landing-md-under">{t('inspectLanding:dueUnder')}</p>
        </figcaption>
      </figure>
      <div className="inspect-landing-md-cols inspect-landing-md-sec">
        <section>
          <h3 className="inspect-landing-md-h">{t('inspectLanding:dueH1')}</h3>
          <p className="inspect-landing-md-p">{t('inspectLanding:dueP1')}</p>
        </section>
        <section>
          <h3 className="inspect-landing-md-h">{t('inspectLanding:dueH2')}</h3>
          <dl className="inspect-landing-md-key">
            <dt>{chip('scheduled', t('inspectLanding:chipScheduled'))}</dt>
            <dd>{t('inspectLanding:dueKeyScheduled')}</dd>
            <dt>{timedChip('due-soon', t('inspectLanding:chipDueSoon'), t('inspectLanding:exDueSoonTime'))}</dt>
            <dd>{t('inspectLanding:dueKeyDueSoon')}</dd>
            <dt>{timedChip('overdue', t('inspectLanding:chipOverdue'), t('inspectLanding:exOverdueTime'))}</dt>
            <dd>{t('inspectLanding:dueKeyOverdue')}</dd>
          </dl>
        </section>
      </div>
    </div>
  );
}

function resultsBody(t) {
  return (
    <div>
      <p className="inspect-landing-md-lead">{t('inspectLanding:resultsLead')}</p>
      <div className="inspect-landing-md-pair">
        <figure className="inspect-landing-md-ph">
          {appShot('results', 0, t)}
          <figcaption>
            <b>{t('inspectLanding:resultsBeforeLabel')}</b> {t('inspectLanding:resultsBeforeText')}
          </figcaption>
        </figure>
        <span className="inspect-landing-md-arrow" aria-hidden="true">
          <span className="material-symbols-outlined">arrow_forward</span>
        </span>
        <figure className="inspect-landing-md-ph">
          {appShot('results', 1, t)}
          <figcaption>
            <b>{t('inspectLanding:resultsAfterLabel')}</b> {t('inspectLanding:resultsAfterText')}
          </figcaption>
        </figure>
      </div>
      <div className="inspect-landing-md-cols inspect-landing-md-sec">
        <section>
          <h3 className="inspect-landing-md-h">{t('inspectLanding:resultsH1')}</h3>
          <p className="inspect-landing-md-p">{t('inspectLanding:resultsP1')}</p>
        </section>
        <section>
          <h3 className="inspect-landing-md-h">{t('inspectLanding:resultsH2')}</h3>
          <p className="inspect-landing-md-p">{t('inspectLanding:resultsP2')}</p>
        </section>
      </div>
    </div>
  );
}

function repairsBody(t) {
  return (
    <div>
      <p className="inspect-landing-md-lead">{t('inspectLanding:repairsLead')}</p>
      <ol className="inspect-landing-md-life">
        <li className="inspect-landing-md-stage">
          {chip('fail', t('inspectLanding:chipFail'))}
          <p>{t('inspectLanding:repairsStage1')}</p>
        </li>
        <li className="inspect-landing-md-stage">
          <span className="inspect-landing-md-act">
            <span className="material-symbols-outlined">task_alt</span>
            {t('inspectLanding:repairsMarkCorrected')}
          </span>
          <p>{t('inspectLanding:repairsStage2')}</p>
        </li>
        <li className="inspect-landing-md-stage">
          {chip('corrected', t('inspectLanding:chipCorrected'))}
          <p>{t('inspectLanding:repairsStage3')}</p>
        </li>
      </ol>
      <div className="inspect-landing-md-split inspect-landing-md-sec">
        <section>
          <h3 className="inspect-landing-md-h">{t('inspectLanding:repairsH1')}</h3>
          <p className="inspect-landing-md-p">{t('inspectLanding:repairsP1')}</p>
          <p className="inspect-landing-md-p inspect-landing-md-gap">{t('inspectLanding:repairsP2')}</p>
          <p className="inspect-landing-md-p inspect-landing-md-gap inspect-landing-md-muted">
            {t('inspectLanding:repairsP3')}
          </p>
        </section>
        <figure className="inspect-landing-md-fig inspect-landing-md-fig-flush">
          {shotFrame('repairs', t)}
        </figure>
      </div>
    </div>
  );
}

function skippedBody(t) {
  return (
    <div>
      <p className="inspect-landing-md-lead">{t('inspectLanding:skippedLead')}</p>
      <div className="inspect-landing-md-track" aria-hidden="true">
        <div className="inspect-landing-md-track-chips">
          {chip('overdue', t('inspectLanding:chipOverdue'))}
          {chip('neglected', t('inspectLanding:chipNeglected'))}
        </div>
        <div className="inspect-landing-md-track-bar">
          <span className="inspect-landing-md-track-a" />
          <span className="inspect-landing-md-track-b" />
        </div>
        <span className="inspect-landing-md-track-dot" />
        <span className="inspect-landing-md-track-tick" />
        <span className="inspect-landing-md-track-l inspect-landing-md-track-l-start">
          {t('inspectLanding:skippedTrackDue')}
        </span>
        <span className="inspect-landing-md-track-l inspect-landing-md-track-l-warn">
          {t('inspectLanding:skippedTrackWarn')}
        </span>
      </div>
      <p className="inspect-landing-md-p">{t('inspectLanding:skippedP1')}</p>
      <figure className="inspect-landing-md-fig inspect-landing-md-fig-wide">
        {shotFrame('skipped', t)}
        <figcaption>
          <p className="inspect-landing-md-under">{t('inspectLanding:skippedUnder')}</p>
        </figcaption>
      </figure>
      <section className="inspect-landing-md-sec">
        <h3 className="inspect-landing-md-h">{t('inspectLanding:skippedH1')}</h3>
        <p className="inspect-landing-md-p">{t('inspectLanding:skippedP2')}</p>
      </section>
    </div>
  );
}

var GLOSSARY = [
  ['glossTotal', 'glossTotalDef'],
  ['glossOverdue', 'glossOverdueDef'],
  ['glossPassed', 'glossPassedDef'],
  ['glossUpcoming', 'glossUpcomingDef'],
  ['glossOpen', 'glossOpenDef'],
  ['glossNeglected', 'glossNeglectedDef'],
];

var RECIPE = [
  { label: 'recipePass', pct: 'recipePassPct', cls: 'inspect-landing-md-seg1' },
  { label: 'recipeFinished', pct: 'recipeFinishedPct', cls: 'inspect-landing-md-seg2' },
  { label: 'recipeOnTime', pct: 'recipeOnTimePct', cls: 'inspect-landing-md-seg3' },
];

function pictureBody(t) {
  return (
    <div>
      <p className="inspect-landing-md-lead">{t('inspectLanding:pictureLead')}</p>
      <figure className="inspect-landing-md-fig">
        {shotFrame('picture', t)}
        <figcaption>
          <p className="inspect-landing-md-under">{t('inspectLanding:pictureUnder')}</p>
        </figcaption>
      </figure>
      <section className="inspect-landing-md-sec">
        <h3 className="inspect-landing-md-h">{t('inspectLanding:pictureH1')}</h3>
        <dl className="inspect-landing-md-gloss">
          {GLOSSARY.map(function (pair) {
            return (
              <div key={pair[0]}>
                <dt>{t('inspectLanding:' + pair[0])}</dt>
                <dd>{t('inspectLanding:' + pair[1])}</dd>
              </div>
            );
          })}
        </dl>
      </section>
      <section className="inspect-landing-md-sec">
        <h3 className="inspect-landing-md-h">{t('inspectLanding:pictureH2')}</h3>
        <div className="inspect-landing-md-recipe" aria-hidden="true">
          <div className="inspect-landing-md-recipe-bar">
            {RECIPE.map(function (part) {
              return <span key={part.label} className={part.cls} />;
            })}
          </div>
          <div className="inspect-landing-md-recipe-l">
            {RECIPE.map(function (part) {
              return (
                <span key={part.label}>
                  <b>{t('inspectLanding:' + part.pct)}</b>
                  {t('inspectLanding:' + part.label)}
                </span>
              );
            })}
          </div>
        </div>
        <p className="inspect-landing-md-p inspect-landing-md-gap-lg">{t('inspectLanding:pictureP')}</p>
        <div className="inspect-landing-md-bands">
          {chip('scheduled', t('inspectLanding:bandGood'))}
          {chip('due-soon', t('inspectLanding:bandFair'))}
          {chip('fail', t('inspectLanding:bandPoor'))}
        </div>
      </section>
    </div>
  );
}

var BODIES = {
  due: dueBody,
  results: resultsBody,
  repairs: repairsBody,
  skipped: skippedBody,
  picture: pictureBody,
};

export default class LearnMoreDialog extends Component {
  render() {
    var props = this.props;
    var t = props.t;
    var row = props.row;
    var build = BODIES[row];
    if (!build) return null;
    var sent = !!props.requestSent;

    return (
      <SlateDialog
        open={!!props.open}
        onClose={props.onClose}
        title={t('inspectLanding:' + row + 'Title')}
        maxWidth="md"
        className="inspect-landing-dialog"
        contentPadding={false}
        actions={[
          {
            key: 'close',
            label: t('inspectLanding:close'),
            variant: 'secondary',
            onClick: props.onClose,
          },
        ]}
      >
        <div className="inspect-landing-dialog-body">
          {build(t)}
          <div className="inspect-landing-md-cta">
            <BlueButton
              variant="primary"
              label={t(sent ? 'inspectLanding:requestSent' : 'inspectLanding:requestAccess')}
              disabled={sent}
              onClick={props.onRequestAccess}
            />
          </div>
        </div>
      </SlateDialog>
    );
  }
}

LearnMoreDialog.propTypes = {
  row: PropTypes.oneOf(LEARN_MORE_ROWS).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRequestAccess: PropTypes.func.isRequired,
  requestSent: PropTypes.bool,
  t: PropTypes.func.isRequired,
};
